import { API } from '@/utils/API';
import { useState, useCallback, useEffect, useRef } from 'react';

export interface Resource {
  id: string;
  name: string;
}

export type ResourceType = 'Study' | 'StudySite' | 'Submission';

interface UseFetchResourcesByTypeResult<T> {
  fetchResources: (
    resourceType: ResourceType,
    options?: RequestInit
  ) => Promise<void>;
  clearError: () => void;
  resources: T[];
  isLoading: boolean;
  error: string | null;
}

const getEndpoint = (resourceType: ResourceType): string => {
  switch (resourceType) {
    case 'Study':
      return '/studies';
    case 'StudySite':
      return '/study_sites';
    case 'Submission':
      return '/submissions';
    default:
      throw new Error(`Invalid resource type: ${resourceType}`);
  }
};

export const useFetchResourcesByType = <
  T extends Resource,
>(): UseFetchResourcesByTypeResult<T> => {
  const [resources, setResources] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchResources = useCallback(
    async (resourceType: ResourceType, options: RequestInit = {}) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const newAbortController = new AbortController();
      abortControllerRef.current = newAbortController;

      setIsLoading(true);
      setError(null);

      try {
        const endpoint = getEndpoint(resourceType);

        const response = await API.authenticated.admin.fetch(endpoint, {
          signal: newAbortController.signal,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          ...options,
        });

        if (!response.ok) {
          let errorMessage = `HTTP error! status: ${response.status}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
          } catch {
            // Use default error message if parsing fails
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();

        // Handle different response formats
        const resourceArray: T[] = data.resources || data.data || data || [];

        setResources(resourceArray);
      } catch (err) {
        // Don't update state for aborted requests
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch resources';
        console.error(`Error fetching ${resourceType}:`, err);
        setError(errorMessage);
        setResources([]);
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    },
    [] // Remove abortController from dependencies to prevent infinite loop
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Cleanup: abort any pending requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    fetchResources,
    clearError,
    resources,
    isLoading,
    error,
  };
};
